import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { Mask } from "@/utils/mask";
import { Textarea } from "@/components/ui/textarea";

interface TransactionCodeItem {
  payment_form_id: number;
  code_transaction: string;
  value: string;
  payment_form_name: string;
  transaction_id?: number;
  bank_account_id?: number;
  payer_name?: string;
  date_hour?: string;
  observation?: string;
}

interface TransactionCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (transactionCodes: TransactionCodeItem[], trackingCode?: string) => Promise<void>;
  salePaymentForms?: Array<{
    id: number;
    value: string;
    payment_form: { id: number; name: string; bank_account_id?: number };
  }>;
  bankAccounts?: Array<{ id: number; surname: string }>;
  saleTypeId?: number;
}

export const TransactionCodeModal = ({
  isOpen,
  onClose,
  onConfirm,
  salePaymentForms = [],
  bankAccounts = [],
  saleTypeId,
}: TransactionCodeModalProps) => {
  const [transactionCodes, setTransactionCodes] = useState<
    TransactionCodeItem[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [trackingCode, setTrackingCode] = useState("");

  const isSedex = saleTypeId === 4;

  useEffect(() => {
    if (isOpen && salePaymentForms && salePaymentForms.length > 0) {
      const initialCodes = salePaymentForms.map((item) => {
        const baseItem: TransactionCodeItem = {
          payment_form_id: item.payment_form.id,
          code_transaction: "",
          value: item.value,
          payment_form_name: item.payment_form.name,
          transaction_id: item.id,
          observation: "",
        };

        if (item.payment_form.id === 5 || item.payment_form.id === 8) {
          baseItem.bank_account_id =
            item.payment_form.bank_account_id || undefined;
          baseItem.payer_name = "";
          baseItem.date_hour = "";
        }

        return baseItem;
      });
      setTransactionCodes(initialCodes);
    } else if (isOpen) {
      setTransactionCodes([
        {
          payment_form_id: 1,
          code_transaction: "",
          value: "0.00",
          payment_form_name: "Forma de Pagamento",
          observation: "",
        },
      ]);
    } else {
      setTransactionCodes([]);
      setTrackingCode("");
    }
  }, [isOpen, salePaymentForms?.length]);

  const handleCodeChange = (index: number, code: string) => {
    setTransactionCodes((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, code_transaction: code } : item
      )
    );
  };

  const handlePayerNameChange = (index: number, payerName: string) => {
    setTransactionCodes((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, payer_name: payerName } : item
      )
    );
  };

  const handleDateHourChange = (index: number, dateHour: string) => {
    setTransactionCodes((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, date_hour: dateHour } : item
      )
    );
  };

  const handleObservationChange = (index: number, observation: string) => {
    setTransactionCodes((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, observation: observation } : item
      )
    );
  };

  const handleConfirm = async () => {
    // Validate tracking code for Sedex
    if (isSedex && !trackingCode.trim()) return;

    const isValid = transactionCodes.every((item) => {
      const trimmedCode = item.code_transaction.trim();
      const requiresCode = isCodeRequired(item.payment_form_id);

      if (requiresCode && !trimmedCode.length) return false;

      // Para dinheiro, exige exatamente 5 dígitos
      if (isMoney(item.payment_form_id) && trimmedCode.length !== 5) return false;

      if (isManualPixPayment(item.payment_form_id)) {
        return (
          item.bank_account_id !== undefined &&
          item.payer_name?.trim() &&
          item.date_hour?.trim()
        );
      }

      return true;
    });

    if (!isValid) return;

    setLoading(true);
    try {
      await onConfirm(transactionCodes, isSedex ? trackingCode.trim() : undefined);
      onClose();
    } catch (error) {
      console.error("Error confirming transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentFormDisplayName = (
    item: TransactionCodeItem,
    index: number
  ) => {
    const formattedValue = Mask.formatCurrency(parseFloat(item.value));
    return `${item.payment_form_name} - ${formattedValue} (${index + 1}º)`;
  };

  const isManualPixPayment = (paymentFormId: number) =>
    paymentFormId === 5 || paymentFormId === 8;

  const isValeCompra = (paymentFormId: number) => paymentFormId === 9;

  const isReinvestmentOrAdvancePayment = (paymentFormId: number) =>
    paymentFormId === 4 || paymentFormId === 10;

  const isMoney = (paymentFormId: number) => paymentFormId === 2;

  const isCodeRequired = (paymentFormId: number) =>
    !isManualPixPayment(paymentFormId) && !isValeCompra(paymentFormId) && !isReinvestmentOrAdvancePayment(paymentFormId);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isSedex ? "Código de Rastreio e Transação" : "Códigos de Transação"}
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            {isSedex
              ? "Preencha o código de rastreio e as informações de pagamento."
              : "Preencha as informações necessárias para cada forma de pagamento."}
          </p>
        </DialogHeader>
        <div className="grid gap-6 py-4 max-h-[500px] overflow-y-auto">
          {isSedex && (
            <div className="border rounded-lg p-4 space-y-4 bg-blue-50 dark:bg-blue-900/20">
              <Label className="text-left font-medium text-base text-blue-700 dark:text-blue-300">
                Informações do SEDEX
              </Label>
              <div className="grid gap-2">
                <Label htmlFor="tracking-code" className="text-blue-700 dark:text-blue-300">
                  Código de Rastreio *
                </Label>
                <Input
                  id="tracking-code"
                  value={trackingCode}
                  onChange={(e) => setTrackingCode(e.target.value.toUpperCase())}
                  placeholder="Ex: BR123456789BR"
                  className="uppercase"
                  autoFocus
                />
              </div>
            </div>
          )}
          {transactionCodes.map((item, index) => {
            const isManualPix = isManualPixPayment(item.payment_form_id);
            const isVale = isValeCompra(item.payment_form_id);
            const isReinvestmentOrAdvance = isReinvestmentOrAdvancePayment(item.payment_form_id);
            const isMoneyPayment = isMoney(item.payment_form_id);

            return (
              <div
                key={`${item.transaction_id || index}`}
                className="border rounded-lg p-4 space-y-4"
              >
                <Label className="text-left font-medium text-base">
                  {getPaymentFormDisplayName(item, index)}
                </Label>

                {!isManualPix && !isVale && !isReinvestmentOrAdvance && (
                  <div className="grid gap-2">
                    <Label htmlFor={`transaction-code-${index}`}>
                      Código de Transação *
                    </Label>
                    <Input
                      id={`transaction-code-${index}`}
                      value={item.code_transaction}
                      onChange={(e) => {
                        let value = isMoneyPayment
                          ? e.target.value.replace(/\D/g, '').slice(0, 5)
                          : e.target.value;
                        handleCodeChange(index, value);
                      }}
                      placeholder={isMoneyPayment ? "Digite 5 números" : "Digite o código de transação"}
                      autoFocus={!isSedex && index === 0}
                      inputMode={isMoneyPayment ? "numeric" : "text"}
                      maxLength={isMoneyPayment ? 5 : undefined}
                    />
                  </div>
                )}

                {isManualPix && (
                  <>
                    <div className="grid gap-2">
                      <Label htmlFor={`bank-account-${index}`}>
                        Conta Bancária
                      </Label>
                      <div className="flex h-10 w-full rounded-md border border-input bg-gray-50 px-3 py-2 text-sm ring-offset-background">
                        {bankAccounts.find(
                          (acc) => acc.id === item.bank_account_id
                        )?.surname || `Conta ID: ${item.bank_account_id}`}
                      </div>
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor={`payer-name-${index}`}>
                        Nome do Pagador
                      </Label>
                      <Input
                        id={`payer-name-${index}`}
                        value={item.payer_name || ""}
                        onChange={(e) =>
                          handlePayerNameChange(index, e.target.value)
                        }
                        placeholder="Digite o nome do pagador"
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor={`date-hour-${index}`}>Data e Hora</Label>
                      <Input
                        id={`date-hour-${index}`}
                        type="datetime-local"
                        value={item.date_hour || ""}
                        onChange={(e) =>
                          handleDateHourChange(index, e.target.value)
                        }
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor={`observation-${index}`}>
                        Observação (opcional)
                      </Label>
                      <Textarea
                        id={`observation-${index}`}
                        value={item.observation || ""}
                        onChange={(e) =>
                          handleObservationChange(index, e.target.value)
                        }
                        placeholder="Adicionar uma observação sobre o pagamento"
                        className="min-h-[80px] resize-y"
                      />
                    </div>
                  </>
                )}

                {!isManualPix && (
                  <div className="grid gap-2">
                    <Label htmlFor={`observation-${index}`}>
                      Observação (opcional)
                    </Label>
                    <Textarea
                      id={`observation-${index}`}
                      value={item.observation || ""}
                      onChange={(e) =>
                        handleObservationChange(index, e.target.value)
                      }
                      placeholder="Adicionar uma observação sobre o pagamento"
                      className="min-h-[80px] resize-y"
                    />
                  </div>
                )}
            </div>
          );
        })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={
              loading ||
              (isSedex && !trackingCode.trim()) ||
              transactionCodes.some((item) => {
                const trimmedCode = item.code_transaction.trim();
                const requiresCode = isCodeRequired(item.payment_form_id);

                if (requiresCode && !trimmedCode.length) return true;

                // Para dinheiro, exige exatamente 5 dígitos
                if (isMoney(item.payment_form_id) && trimmedCode.length !== 5) return true;

                if (isManualPixPayment(item.payment_form_id)) {
                  return (
                    item.bank_account_id === undefined ||
                    !item.payer_name?.trim() ||
                    !item.date_hour?.trim()
                  );
                }

                return false;
              })
            }
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
